import { useMemo, useEffect } from "react";
import {
  JobTaskDetailSchema,
  OoSParcelSchema,
} from "@augmodo/lebron-browser-client";
import { useAtlasPlanogram } from "@labeling-tools/api/hooks";

/**
 * Pure planogram-id resolution, extracted so callers that only have the task
 * still in scope (e.g. right before its data is cleared from cache after
 * submission) can resolve it without pulling in useTaskPlanogram's data
 * fetching. Tolerant of undefined task/parcel.
 */
export const resolveTaskPlanogramId = (
  task: JobTaskDetailSchema | undefined | null,
  parcel: OoSParcelSchema | undefined | null,
): string | undefined => {
  const taskData = task?.data as any;
  const isBoxDrawTask = !!taskData?.box_draw_task_id;

  // Prefer a direct planogram_id on the task data (e.g. set by backend or box-draw flow)
  const directPlanogramId = taskData?.planogram_id as string | undefined;

  // For box-draw tasks, aisle_index is set directly on task data.
  // For non-box-draw tasks, resolve aisle via the parcel's empty_area_to_aisle mapping.
  // Both paths use the parcel's aisle_to_planogram to resolve the planogram.
  const emptyAreaIndex = (taskData?.query_empty_area_index ??
    task?.data?.empty_area_index) as number;
  const aisleIndex = isBoxDrawTask
    ? (taskData?.aisle_index as number)
    : (parcel?.query?.empty_area_to_aisle?.[emptyAreaIndex] as number);
  const mappedPlanogramId = parcel?.query?.aisle_to_planogram?.[aisleIndex] as
    | string
    | undefined;

  return directPlanogramId || mappedPlanogramId;
};

export const useTaskPlanogram = (
  task: JobTaskDetailSchema,
  parcel: OoSParcelSchema,
) => {
  const taskData = task.data as any;
  const isBoxDrawTask = !!taskData?.box_draw_task_id;
  const directPlanogramId = taskData?.planogram_id as string | undefined;
  const emptyAreaIndex = (taskData?.query_empty_area_index ??
    task.data?.empty_area_index) as number;
  const aisleIndex = isBoxDrawTask
    ? (taskData?.aisle_index as number)
    : (parcel.query?.empty_area_to_aisle?.[emptyAreaIndex] as number);
  const mappedPlanogramId = parcel.query?.aisle_to_planogram?.[aisleIndex] as
    | string
    | undefined;

  const planogramId = resolveTaskPlanogramId(task, parcel);

  const resolveWarning = useMemo(() => {
    // No warning needed if the task provides a direct planogram ID
    if (directPlanogramId) return null;

    if (emptyAreaIndex == null) {
      console.warn("[useTaskPlanogram] task.data.empty_area_index is missing");
      return "Missing empty area index on task data";
    }
    if (aisleIndex == null) {
      console.warn(
        `[useTaskPlanogram] No aisle mapping for empty_area_index=${emptyAreaIndex}`,
      );
      return `No aisle mapping found for empty area ${emptyAreaIndex}`;
    }
    if (!mappedPlanogramId) {
      console.warn(
        `[useTaskPlanogram] No planogram mapped for aisle_index=${aisleIndex}`,
      );
      return `No planogram mapped for aisle ${aisleIndex}`;
    }
    return null;
  }, [directPlanogramId, emptyAreaIndex, aisleIndex, mappedPlanogramId]);

  const {
    data: planogramData,
    isLoading: isPlanogramLoading,
    isError: isPlanogramError,
    error: planogramError,
  } = useAtlasPlanogram(planogramId);

  // Warm the browser HTTP cache for the stitched background image so it's ready
  // before the user opens the PlanogramModal. No crossOrigin — PlanoLabelBoxes
  // renders it as a plain <img>, not canvas.
  useEffect(() => {
    const url = planogramData?.image_url;
    if (!url) return;
    const img = new Image();
    img.src = url;
  }, [planogramData?.image_url]);

  // When there's no planogram ID to look up, the query is disabled
  // and isLoading stays true forever — treat that as "not loading"
  const effectivelyLoading = !!planogramId && isPlanogramLoading;

  // Debug: log the full resolution chain so we can diagnose "No planogram image found"
  console.debug("[useTaskPlanogram] Resolution chain:", {
    directPlanogramId,
    isBoxDrawTask,
    emptyAreaIndex,
    aisleIndex,
    mappedPlanogramId,
    resolvedPlanogramId: planogramId,
    planogramHasImage: !!planogramData?.image_url,
    planogramError: isPlanogramError ? planogramError : null,
  });

  return {
    planogramData,
    isPlanogramLoading: effectivelyLoading,
    isPlanogramError,
    resolveWarning,
    aisleIndex,
    planogramId,
  };
};
